const createStorefrontClient = () => {
    const executeQuery = async (query, variables = {}) => {
        const cfg = (typeof window !== 'undefined' && window.STOREFRONT_CONFIG) ? window.STOREFRONT_CONFIG : {};
        const requestUrl = `https://${cfg.storeDomain}/api/${cfg.apiVersion}/graphql`;
        const res = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': cfg.publicAccessToken,
            },
            body: JSON.stringify({ query, variables })
        });

        if (!res.ok) {
            const text = await res.text();
            throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
        }

        const json = await res.json();
        if (json.errors && json.errors.length) {
            throw new Error(json.errors[0].message || 'GraphQL 错误');
        }
        return json;
    };

    return {
        cart: {
            create: async (input) => {
                const mutation = `
                    mutation cartCreate($input: CartInput!) {
                        cartCreate(input: $input) {
                            cart { id checkoutUrl }
                            userErrors { field message }
                        }
                    }
                `;
                return executeQuery(mutation, { input });
            }
        }
    };
};

// 自定义元素：点击即创建购物车并跳转结账
customElements.define('buy-now-btn', class extends HTMLElement {
    constructor() {
        super();
        this.client = createStorefrontClient();
        this.isProcessing = false;
        this.init();
    }

    init() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.style.cursor = 'pointer';
        this.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleClick(e);
            }
        });
    }

    async handleClick(e) {
        e.preventDefault();
        if (this.isProcessing) return;

        try {
            this.isProcessing = true;
            this.setLoadingState(true);

            const variantIdRaw = this.dataset.variantId;
            const quantity = parseInt(this.dataset.variantQuantity || '1', 10);
            if (!variantIdRaw) throw new Error('缺少商品变体 ID');
            if (!quantity || quantity < 1) throw new Error('商品数量无效');

            const merchandiseId = variantIdRaw.startsWith('gid://shopify/ProductVariant/')
                ? variantIdRaw
                : `gid://shopify/ProductVariant/${variantIdRaw}`;

            const cartInput = {
                lines: [{ merchandiseId, quantity }]
            };

            const res = await this.client.cart.create(cartInput);

            const userErrors = res.data?.cartCreate?.userErrors || [];
            if (userErrors.length) {
                throw new Error(userErrors[0].message || '创建购物车失败');
            }

            const checkoutUrl = res.data?.cartCreate?.cart?.checkoutUrl;
            if (!checkoutUrl) throw new Error('无法获取结账链接');

            // 跳转结账
            window.location.href = checkoutUrl;
        } catch (err) {
            this.handleError(err);
        } finally {
            this.isProcessing = false;
            this.setLoadingState(false);
        }
    }

    handleError(error) {
        console.error('结账失败:', error);
        const message = error?.message || '出现未知错误，请稍后再试';
        if (window.showToast) {
            window.showToast(message, 'error');
        } else {
            alert(message);
        }
    }

    setLoadingState(isLoading) {
        if (isLoading) {
            this.setAttribute('data-loading', 'true');
            this.style.pointerEvents = 'none';
            this.style.opacity = '0.7';
            if (this.dataset.loadingText) {
                this._originalText = this.textContent;
                this.textContent = this.dataset.loadingText;
            }
        } else {
            this.removeAttribute('data-loading');
            this.style.pointerEvents = '';
            this.style.opacity = '';
            if (this._originalText) {
                this.textContent = this._originalText;
                delete this._originalText;
            }
        }
    }
});

// custom-checkout-button: adds selected variant (+ any free gifts) to cart then redirects to checkout
customElements.define('custom-checkout-button', class extends HTMLElement {
    constructor() {
        super();
        this.isProcessing = false;
        this.init();
    }

    init() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.style.cursor = 'pointer';
        this.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleClick(e);
            }
        });
    }

    async handleClick(e) {
        e.preventDefault();
        if (this.isProcessing) return;

        try {
            this.isProcessing = true;
            this.setLoadingState(true);

            const formId = this.dataset.formId;
            const form = (formId && document.getElementById(formId)) || this.closest('form');
            if (!form) throw new Error('Product form not found');
            const actualFormId = form.getAttribute('id');

            // Read selected bundle card variant first
            const bcScriptEl = document.querySelector(`[id^="bc-data-"][id*="${actualFormId}"]`);
            if (bcScriptEl) {
                try {
                    const bcData = JSON.parse(bcScriptEl.textContent);
                    const list = document.getElementById(bcData.listId);
                    const sel = list && list.querySelector('.bundle-picker__item.is-selected');
                    const selectedVid = sel && sel.getAttribute('data-variant-id');
                    if (selectedVid) {
                        const idInp = form.querySelector('[name="id"]');
                        if (idInp) { idInp.disabled = false; idInp.value = selectedVid; }
                    }
                } catch (bcErr) { console.warn('[BC] custom-checkout bundle read error:', bcErr); }
            }

            const idInput = form.querySelector('[name="id"]');
            const variantId = parseInt(idInput && idInput.value, 10);
            if (!variantId) throw new Error('Missing variant ID');

            const quantityInput = document.querySelector(`[name="quantity"][form="${actualFormId}"]`)
                || form.querySelector('[name="quantity"]');
            const quantity = parseInt((quantityInput && quantityInput.value) || '1', 10) || 1;

            const items = [{ id: variantId, quantity }];

            // Include any free-gift items on the page
            document.querySelectorAll('.free-gift_item[data-product-id]').forEach(btn => {
                const giftId = parseInt(btn.getAttribute('data-product-id'), 10);
                if (giftId) items.push({ id: giftId, quantity });
            });

            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const response = await fetch(`${root}cart/add.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ items })
            });

            const json = await response.json();
            if (!response.ok) throw new Error(json.description || json.message || 'Failed to add to cart');

            window.location.href = `${root}checkout`;
        } catch (err) {
            this.handleError(err);
        } finally {
            this.isProcessing = false;
            this.setLoadingState(false);
        }
    }

    handleError(error) {
        console.error('结账失败:', error);
        const message = error?.message || '出现未知错误，请稍后再试';
        if (window.showToast) {
            window.showToast(message, 'error');
        } else {
            alert(message);
        }
    }

    setLoadingState(isLoading) {
        if (isLoading) {
            this.setAttribute('data-loading', 'true');
            this.style.pointerEvents = 'none';
            this.style.opacity = '0.7';
            if (this.dataset.loadingText) {
                this._originalText = this.textContent;
                this.textContent = this.dataset.loadingText;
            }
        } else {
            this.removeAttribute('data-loading');
            this.style.pointerEvents = '';
            this.style.opacity = '';
            if (this._originalText) {
                this.textContent = this._originalText;
                delete this._originalText;
            }
        }
    }
});